import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaTimes, FaPaperPlane, FaArrowRight, FaArrowLeft,
  FaCheckCircle, FaMapMarkerAlt, FaCalendarAlt,
  FaUsers, FaUser, FaPhone, FaEnvelope
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import contactService from '../services/contactService';
import locationService from '../services/locationService';

const FALLBACK_DESTINATIONS = [
  'Shimla', 'Manali', 'Dharamshala', 'McLeod Ganj', 'Spiti Valley',
  'Kullu', 'Kasol', 'Bir Billing', 'Dalhousie', 'Kinnaur',
  'Kufri', 'Solang Valley', 'Rohtang Pass', 'Triund', 'Khajjiar',
];

const IC =
  'w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition text-sm'

const TODAY = new Date().toISOString().split('T')[0]

/* ---------- Sub-components to keep cognitive complexity low ---------- */

function SuccessView({ name, onClose }) {
  return (
    <div className="flex flex-col items-center text-center py-4 space-y-4">
      <FaCheckCircle className="text-green-500 text-5xl" />
      <h3 className="text-xl font-bold text-gray-900">Request Sent!</h3>
      <p className="text-gray-600 text-sm">
        Thank you, <span className="font-semibold">{name}</span>! Our travel expert will reach out
        to you on WhatsApp or email shortly.
      </p>
      <p className="text-xs text-gray-400">Usually within 2 hours during business hours.</p>
      <button
        onClick={onClose}
        className="mt-2 w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 rounded-lg transition"
      >
        Close
      </button>
    </div>
  )
}
SuccessView.propTypes = { name: PropTypes.string.isRequired, onClose: PropTypes.func.isRequired }

function Step1Form({ step1, onDestinationChange, onStep1Change, onNext, popularDestinations }) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!showPicker) return undefined
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPicker])

  const toggleDest = (d) => {
    const next = step1.destinations.includes(d)
      ? step1.destinations.filter((x) => x !== d)
      : [...step1.destinations, d]
    onDestinationChange(next)
  }

  const adjustAdults = (delta) => {
    onStep1Change('adults', Math.max(1, (step1.adults || 1) + delta))
  }

  const adjustChildren = (delta) => {
    const next = Math.max(0, (step1.children || 0) + delta)
    const ages = [...(step1.child_ages || [])]
    while (ages.length < next) ages.push(0)
    ages.splice(next)
    onStep1Change('children', next)
    onStep1Change('child_ages', ages)
  }

  const setChildAge = (idx, age) => {
    const ages = [...(step1.child_ages || [])]
    ages[idx] = Number(age)
    onStep1Change('child_ages', ages)
  }

  return (
    <form onSubmit={onNext} className="space-y-4" noValidate>
      {/* Destination Tile Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <FaMapMarkerAlt className="inline mr-1 text-primary-500" />
          Where do you want to go? <span className="text-red-500">*</span>
        </label>
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setShowPicker((p) => !p)}
            className={`w-full text-left px-4 py-2.5 border rounded-lg transition text-sm flex flex-wrap gap-1.5 items-center min-h-[42px] ${
              showPicker
                ? 'border-primary-500 ring-2 ring-primary-200'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {step1.destinations.length === 0 ? (
              <span className="text-gray-400">e.g. Manali, Shimla, Spiti…</span>
            ) : (
              step1.destinations.map((d) => (
                <span
                  key={d}
                  className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 border border-primary-200 text-xs font-medium px-2 py-0.5 rounded-full"
                >
                  {d}
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.stopPropagation()
                      toggleDest(d)
                    }}
                    aria-label={`Remove ${d}`}
                    className="ml-0.5 text-primary-400 hover:text-primary-700 leading-none"
                  >
                    &times;
                  </button>
                </span>
              ))
            )}
          </button>
          {showPicker && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-3 max-h-56 overflow-y-auto">
              <div className="grid grid-cols-3 gap-1.5">
                {popularDestinations.map((d) => {
                  const selected = step1.destinations.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDest(d)}
                      className={`text-xs px-2 py-2 rounded-lg border text-center transition font-medium ${
                        selected
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-primary-50 hover:border-primary-300'
                      }`}
                    >
                      {selected ? '✓ ' : ''}
                      {d}
                    </button>
                  )
                })}
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                className="mt-2 w-full text-xs text-primary-600 font-semibold py-1.5 hover:bg-primary-50 rounded-lg transition"
              >
                Done ✓
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="q-date-from" className="block text-sm font-medium text-gray-700 mb-1">
            <FaCalendarAlt className="inline mr-1 text-primary-500" />
            From
          </label>
          <input
            id="q-date-from"
            type="date"
            name="travel_date_from"
            value={step1.travel_date_from}
            onChange={(e) => onStep1Change('travel_date_from', e.target.value)}
            min={TODAY}
            className={IC}
          />
        </div>
        <div>
          <label htmlFor="q-date-to" className="block text-sm font-medium text-gray-700 mb-1">
            <FaCalendarAlt className="inline mr-1 text-primary-500" />
            To
          </label>
          <input
            id="q-date-to"
            type="date"
            name="travel_date_to"
            value={step1.travel_date_to}
            onChange={(e) => onStep1Change('travel_date_to', e.target.value)}
            min={step1.travel_date_from || TODAY}
            className={IC}
          />
        </div>
      </div>
      <p className="text-xs text-gray-400 -mt-2">Dates are optional if your plans are flexible.</p>

      {/* Adults / Children */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FaUsers className="inline mr-1 text-primary-500" />
          Travelers <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between">
            <span className="text-sm text-gray-600">Adults</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustAdults(-1)}
                disabled={(step1.adults || 1) <= 1}
                className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center text-base leading-none"
              >
                −
              </button>
              <span className="w-5 text-center font-semibold text-sm">{step1.adults || 1}</span>
              <button
                type="button"
                onClick={() => adjustAdults(1)}
                className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center text-base leading-none"
              >
                +
              </button>
            </div>
          </div>
          <div className="border border-gray-300 rounded-lg px-3 py-2 flex items-center justify-between">
            <span className="text-sm text-gray-600">Children</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => adjustChildren(-1)}
                disabled={(step1.children || 0) <= 0}
                className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 flex items-center justify-center text-base leading-none"
              >
                −
              </button>
              <span className="w-5 text-center font-semibold text-sm">{step1.children || 0}</span>
              <button
                type="button"
                onClick={() => adjustChildren(1)}
                className="w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center text-base leading-none"
              >
                +
              </button>
            </div>
          </div>
        </div>
        {(step1.children || 0) > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 font-medium mb-1.5">
              {step1.children === 1 ? "Child's age:" : "Children's ages:"}
            </p>
            <div className="flex flex-wrap gap-2">
              {(step1.child_ages || []).map((age, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`child-age-${idx}`} className="flex items-center gap-1">
                  <label
                    htmlFor={`q-child-age-${idx}`}
                    className="text-xs text-gray-500 whitespace-nowrap"
                  >
                    Child {idx + 1}
                  </label>
                  <select
                    id={`q-child-age-${idx}`}
                    value={age}
                    onChange={(e) => setChildAge(idx, e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:ring-1 focus:ring-primary-500 outline-none"
                  >
                    {Array.from({ length: 18 }, (_, i) => i).map((i) => (
                      <option key={i} value={i}>
                        {i === 0 ? '< 1 yr' : `${i} yr`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        type="submit"
        className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2 mt-2"
      >
        Next <FaArrowRight />
      </button>
    </form>
  )
}
Step1Form.propTypes = {
  step1: PropTypes.object.isRequired,
  onDestinationChange: PropTypes.func.isRequired,
  onStep1Change: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  popularDestinations: PropTypes.array.isRequired,
};

function buildQuoteMessage(step1, dateRange) {
  const childrenCount = step1.children || 0
  const agesStr =
    childrenCount > 0
      ? `\nChildren: ${childrenCount} (ages: ${(step1.child_ages || []).map((a) => (a === 0 ? '<1' : a)).join(', ')})`
      : ''
  return `Destination: ${step1.destinations.join(', ')}\nTravel Dates: ${dateRange}\nAdults: ${step1.adults || 1}${agesStr}`
}

function TravelersPill({ step1 }) {
  const adults = step1.adults || 1
  const children = step1.children || 0
  const adultsLabel = `${adults} Adult${adults === 1 ? '' : 's'}`
  const childSuffix = children === 1 ? '' : 'ren'
  const childrenLabel = children > 0 ? `, ${children} Child${childSuffix}` : ''
  return (
    <span className="bg-primary-50 text-primary-700 border border-primary-200 text-xs font-medium px-3 py-1 rounded-full">
      {'👨'} {adultsLabel}
      {childrenLabel}
    </span>
  )
}
TravelersPill.propTypes = {
  step1: PropTypes.object.isRequired,
}

function Step2Form({ step1, step2, onChange, onBack, onSubmit, submitting }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <div className="flex flex-wrap gap-2 mb-1">
        {step1.destinations.map((d) => (
          <span
            key={d}
            className="bg-primary-50 text-primary-700 border border-primary-200 text-xs font-medium px-3 py-1 rounded-full"
          >
            📍 {d}
          </span>
        ))}
        {step1.travel_date_from && (
          <span className="bg-primary-50 text-primary-700 border border-primary-200 text-xs font-medium px-3 py-1 rounded-full">
            {step1.travel_date_from}
            {step1.travel_date_to ? `  ${step1.travel_date_to}` : ''}
          </span>
        )}
        <TravelersPill step1={step1} />
      </div>

      <div>
        <label htmlFor="q-name" className="block text-sm font-medium text-gray-700 mb-1">
          <FaUser className="inline mr-1 text-primary-500" />
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          id="q-name"
          type="text"
          name="name"
          value={step2.name}
          onChange={onChange}
          required
          autoFocus
          className={IC}
          placeholder="Your full name"
        />
      </div>

      <div>
        <label htmlFor="q-phone" className="block text-sm font-medium text-gray-700 mb-1">
          <FaPhone className="inline mr-1 text-primary-500" />
          WhatsApp / Phone <span className="text-red-500">*</span>
        </label>
        <input
          id="q-phone"
          type="tel"
          name="mobile_number"
          value={step2.mobile_number}
          onChange={onChange}
          required
          className={IC}
          placeholder="+91 XXXXX XXXXX"
        />
      </div>

      <div>
        <label htmlFor="q-email" className="block text-sm font-medium text-gray-700 mb-1">
          <FaEnvelope className="inline mr-1 text-primary-500" />
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          id="q-email"
          type="email"
          name="email"
          value={step2.email}
          onChange={onChange}
          required
          className={IC}
          placeholder="your@email.com"
        />
      </div>

      <p className="text-xs text-gray-400 text-center">
        We respect your privacy. No spam just your customised quote.
      </p>

      <div className="flex gap-3 mt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="flex items-center gap-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm font-medium disabled:opacity-50"
        >
          <FaArrowLeft size={12} /> Back
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Sending...
            </>
          ) : (
            <>
              <FaPaperPlane /> Get My Free Quote
            </>
          )}
        </button>
      </div>
    </form>
  )
}
Step2Form.propTypes = {
  step1: PropTypes.object.isRequired,
  step2: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
}

/* ---------- Main modal ---------- */

const INIT_S1 = {
  destinations: [],
  travel_date_from: '',
  travel_date_to: '',
  adults: 1,
  children: 0,
  child_ages: [],
}
const INIT_S2 = { name: '', mobile_number: '', email: '' }

const QuoteModal = ({ isOpen, onClose, tourTitle }) => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step1, setStep1] = useState({ ...INIT_S1, destinations: tourTitle ? [tourTitle] : [] });
  const [step2, setStep2] = useState({ ...INIT_S2 });
  const [popularDestinations, setPopularDestinations] = useState(FALLBACK_DESTINATIONS);

  useEffect(() => {
    locationService.getAllLocationsFlat().then(data => {
      const names = (data.locations || [])
        .filter(l => l.parent_id !== null && l.is_active)
        .map(l => l.name);
      if (names.length > 0) setPopularDestinations(names);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setSubmitted(false)
      setStep1({ ...INIT_S1, destinations: tourTitle ? [tourTitle] : [] })
      setStep2({ ...INIT_S2 })
    }
  }, [isOpen, tourTitle])

  const handleEsc = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )
  useEffect(() => {
    if (!isOpen) return undefined
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, handleEsc])

  if (!isOpen) return null

  const handleStep1FieldChange = (field, value) => setStep1((prev) => ({ ...prev, [field]: value }))
  const handleStep2Change = (e) =>
    setStep2((prev) => ({ ...prev, [e.target.name]: e.target.value }))

  const handleNext = (e) => {
    e.preventDefault()
    if (!step1.destinations.length) {
      toast.error('Please add at least one destination')
      return
    }
    setStep(2)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!step2.name.trim() || !step2.mobile_number.trim() || !step2.email.trim()) {
      toast.error('Please fill in all contact details')
      return
    }
    const dateRange =
      step1.travel_date_from && step1.travel_date_to
        ? `${step1.travel_date_from} to ${step1.travel_date_to}`
        : step1.travel_date_from || 'Flexible'
    setSubmitting(true)
    try {
      await contactService.submitQuery({
        name: step2.name,
        email: step2.email,
        mobile_number: step2.mobile_number,
        subject: `Free Quote Request: ${step1.destinations.join(', ')}`,
        message: buildQuoteMessage(step1, dateRange),
        query_type: 'quote_request',
      })
      setSubmitted(true)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const progress = step === 1 ? 50 : 100

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop  button role for keyboard/click dismiss */}
      <button
        type="button"
        aria-label="Close quote modal"
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm cursor-default"
        onClick={onClose}
        tabIndex={-1}
      />

      {/* Panel */}
      <dialog
        open
        aria-label="Request Free Quote"
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto m-0 p-0 border-0"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 pt-6 pb-5 rounded-t-2xl">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 text-white/80 hover:text-white transition p-1 rounded-full hover:bg-white/10"
          >
            <FaTimes size={18} />
          </button>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-200 mb-1">
            100% Free No Obligation
          </p>
          <h2 className="text-xl font-bold">Request Free Quote</h2>
          <p className="text-primary-100 text-sm mt-0.5">
            Get a customised itinerary in your inbox
          </p>

          {!submitted && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-primary-200 mb-1">
                <span className={step >= 1 ? 'text-white font-medium' : ''}>Trip Details</span>
                <span className={step >= 2 ? 'text-white font-medium' : ''}>Contact Info</span>
              </div>
              <div className="h-1.5 bg-primary-500 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-right text-xs text-primary-200 mt-1">Step {step} of 2</p>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted && <SuccessView name={step2.name} onClose={onClose} />}
          {!submitted && step === 1 && (
            <Step1Form
              step1={step1}
              onNext={handleNext}
              onDestinationChange={(newDests) =>
                setStep1((prev) => ({ ...prev, destinations: newDests }))
              }
              onStep1Change={handleStep1FieldChange}
              popularDestinations={popularDestinations}
            />
          )}
          {!submitted && step === 2 && (
            <Step2Form
              step1={step1}
              step2={step2}
              onChange={handleStep2Change}
              onBack={() => setStep(1)}
              onSubmit={handleSubmit}
              submitting={submitting}
            />
          )}
        </div>
      </dialog>
    </div>
  )
}

QuoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tourTitle: PropTypes.string,
}

QuoteModal.defaultProps = {
  tourTitle: '',
}

export default QuoteModal
